//#region Imports

import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { LessonInteractor } from '../../interactors/lesson/lesson.interactor';
import { MessagePayload } from '../../models/payloads/message.payload';
import { LessonMediaProxy } from '../../models/proxies/lesson-media.proxy';
import { LessonRatingProxy } from '../../models/proxies/lesson-rating.proxy';
import { LessonProxy } from '../../models/proxies/lesson.proxy';
import { MessageProxy } from '../../models/proxies/message.proxy';
import { TranscriptionTokenProxy } from '../../models/proxies/transcription-token.proxy';
import { EmptyUserProgress, UserProgressProxy } from '../../models/proxies/user-progress.proxy';
import { getErrorMessage } from '../../utils/functions';
import { AuthService } from '../auth/auth.service';
import { CrudRequestParams } from '../../utils/crud';
import { GetMany } from '../../models/proxies/get-many.proxy';

//#endregion

@Injectable({
  providedIn: 'root',
})
export class LessonService {

  //#region Constructors

  constructor(
    private readonly lessonInteractor: LessonInteractor,
    private readonly auth: AuthService,
  ) {}

  //#endregion

  //#region public Properties

  public nextLessonUrlSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');

  public previousLessonUrlSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');

  public completedLessonId: BehaviorSubject<string> = new BehaviorSubject<string>('');

  public completedQuizId: BehaviorSubject<string> = new BehaviorSubject<string>('');

  public shouldLoadSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  //#endregion

  //#region Functions

  public async getProgressBySubject(subjectId: string): Promise<UserProgressProxy[]> {
    const user = this.auth.getCurrentUser();

    if (!user)
      return [];

    const { error, success } = await this.lessonInteractor.getProgressBySubject(user.id, subjectId);

    if (error || !success) return [];

    return Array.isArray(success) ? success : success.data;
  }

  public async getCourseProgressPercentage(courseId: string): Promise<number> {
    const user = this.auth.getCurrentUser();

    if (!user) return 0;

    const { error, success } = await this.lessonInteractor.getCourseProgressPercentage(user.id, courseId);

    if (error || !success) return 0;

    return success;
  }

  public async getUserProgressByLesson(subjectId: string): Promise<UserProgressProxy> {
    const user = this.auth.getCurrentUser();

    if (!user) return EmptyUserProgress;

    const { error, success } = await this.lessonInteractor.getLessonProgressByLesson(user.id, subjectId);

    if (error || !success) return EmptyUserProgress;

    return success.data[0];
  }

  public async getUserProgressByManyLessons(lessonsIds: string[]): Promise<UserProgressProxy[]> {
    const user = this.auth.getCurrentUser();

    if (!user) return [];

    const { error, success } = await this.lessonInteractor.getUserProgressByManyLessons(user.id, lessonsIds);

    if (error || !success) return [];

    return success.data;
  }

  public async getUserProgressByQuiz(quizId: string): Promise<UserProgressProxy> {
    const user = this.auth.getCurrentUser();

    if (!user) return EmptyUserProgress;

    const { error, success } = await this.lessonInteractor.getUserProgressByQuiz(user.id, quizId);

    if (error || !success) return EmptyUserProgress;

    return success.data[0];
  }

  public async getUserProgressByManyQuizzes(quizIds: string[]): Promise<UserProgressProxy[]> {
    const user = this.auth.getCurrentUser();

    if (!user || !Array.isArray(quizIds) || quizIds.length === 0)
      return [];

    const { error, success } = await this.lessonInteractor.getUserProgressByManyQuizzes(user.id, quizIds);

    if (error || !success) return [];

    return Array.isArray(success) ? success : success.data;
  }

  public async markLessonProgress(lessonId: string): Promise<void> {
    await this.lessonInteractor.markLessonProgress(lessonId);
    this.shouldLoadSubject.next(true);
  }

  public async updateLessonVideoTime(lessonId: string, videoTime: string): Promise<void> {
    await this.lessonInteractor.updateLessonVideoTime(lessonId, videoTime);
  }

  public async getLessonsRatingsBySubject(subjectId: string): Promise<LessonRatingProxy[]> {
    const user = this.auth.getCurrentUser();

    if (!user) return [];

    const { error, success } = await this.lessonInteractor.getLessonsRatingsBySubject(user.id, subjectId);

    if (error || !success)
      return [];

    return Array.isArray(success) ? success : success.data;
  }

  public async getMany(crudParams: CrudRequestParams<LessonProxy>): Promise<[GetMany<LessonProxy> | null, string?]> {
    const { error, success } = await this.lessonInteractor.getMany(crudParams);

    if (error || !success)
      return [null, getErrorMessage(error)];

    return [success];
  }

  public async getOneLesson(lessonId: string, includeJoin: boolean = true): Promise<[LessonProxy | null, string?]> {
    const { error, success } = await this.lessonInteractor.getOneLesson(lessonId, includeJoin);

    if (error || !success)
      return [null, getErrorMessage(error)];

    return [success];
  }

  public async getCommentByLesson(lessonId: string): Promise<MessageProxy[]> {
    const { error, success } = await this.lessonInteractor.getCommentByLesson(lessonId);

    if (error || !success)
      return [];

    return success.data;
  }

  public async updateComment(message: MessagePayload, lessonId: string, messageId: string): Promise<[boolean, string | null]> {
    const { error } = await this.lessonInteractor.updateComment(message, lessonId, messageId);

    if (error) {
      return [false, getErrorMessage(error)];
    }

    return [true, null];
  }

  public async deleteCommentById(lessonId: string, messageId: string): Promise<[boolean, string]> {
    const { error } = await this.lessonInteractor.deleteCommentById(lessonId, messageId);

    if (error)
      return [false, getErrorMessage(error)];

    return [true, 'Operação realizada com sucesso'];
  }

  public async makeCommentOnLesson(lessonId: string, comment: MessagePayload): Promise<[boolean, string?]> {
    const { error } = await this.lessonInteractor.makeCommentOnLesson(lessonId, comment);

    if (error)
      return [false, getErrorMessage(error)];

    return [true];
  }

  public async getMediasByLessonId(): Promise<[LessonMediaProxy[] | null, string?]> {
    const { error, success } = await this.lessonInteractor.getMediasByLessonId();

    if (error || !success)
      return [null, getErrorMessage(error)];

    return [Array.isArray(success) ? success : success.data];
  }

  public async getTranscriptionTokensByLessonId(lessonId: string): Promise<TranscriptionTokenProxy[]> {
    const { error, success } = await this.lessonInteractor.getTranscriptionTokensByLessonId(lessonId);

    if (error || !success) return [];

    return success;
  }

  public async getModuleFirstLesson(moduleId: string): Promise<[LessonProxy | null, string?]> {
    const { error, success } = await this.lessonInteractor.getModuleFirstLesson(moduleId);

    if (error || !success)
      return [null, getErrorMessage(error)];

    return [success.data[0]];
  }

  public async resetModuleProgress(moduleId: string): Promise<string> {
    const { error } = await this.lessonInteractor.resetModuleProgress(moduleId);

    if (error)
      return getErrorMessage(error);

    return '';
  }

  public async checkCurrentModuleProgress(moduleId: string): Promise<[boolean, string]> {
    const { error, success } = await this.lessonInteractor.checkCurrentModuleProgress(moduleId);

    if (error || !success)
      return [false, getErrorMessage(error)];

    return [success, ''];
  }

  public async checkCourseCompleted(courseId: string): Promise<boolean> {
    const { error, success } = await this.lessonInteractor.checkCourseCompleted(
      courseId,
    );

    if (error || !success) return false;

    return !!success;
  }

  //#endregion

}
